import React from 'react';
import {
    BodyText,
    Button,
    FlexContainer,
    FlexItem,
    NotificationManager,
    Skeleton,
} from '@placer-ui/components';
import { SupportRequestResultsModalTableRow } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { useSFAccountOwnerDataQuery } from 'features/my-zone/support-requests/components/support-requests-modals/queries/hooks/get-account-owner-query/get-account-owner-query';
import { FormProvider, useForm } from 'react-hook-form';
import { ControlledTextArea } from 'features/my-zone/support-requests/components/support-requests-modals/components/inputs/controlled-textarea';
import { ControlledInputField } from 'components/controlled-input-field';
import { useSendAccountOwnerEmailMutation } from 'features/my-zone/support-requests/components/support-requests-modals/queries/hooks/send-account-owner-email-mutation/send-account-owner-email-mutation';

type SupportCSOwnerFormValues = {
    subject: string;
    message: string;
};

export const SendSupportCSOwnerEmail = ({
    entity,
    onNextHandler,
}: {
    onNextHandler?: () => Promise<void>;
    entity: SupportRequestResultsModalTableRow;
}) => {
    const notificationManager = NotificationManager.getInstance();

    const {
        mutateAsync: sendAccountOwnerEmailMutation,
        isLoading: isLoadingSendAccountOwnerEmailMutation,
    } = useSendAccountOwnerEmailMutation();
    const { data: accountOwnerData, isFetching: isFetchingAccountOwnerData } =
        useSFAccountOwnerDataQuery();
    const { entity_name, entity_address, link_to_report, status } = entity;

    const formController = useForm<SupportCSOwnerFormValues>({
        defaultValues: {
            subject: `Question: ${
                status === 'SensitiveLocation'
                    ? 'Sensitive Location Policy'
                    : 'Nearby Activity Report'
            } - ${entity_name}`,
            message: '',
        },
        mode: 'onBlur',
    });

    const handleSubmit = async (data: SupportCSOwnerFormValues) => {
        try {
            await sendAccountOwnerEmailMutation({
                subject: data.subject,
                message: data.message,
                propertyId: entity.entity_id!,
                propertyAddress: entity_address!,
                propertyName: entity_name!,
                reportLink: link_to_report!,
            });

            notificationManager.addNotification({
                type: 'toast',
                title: 'Your email has been sent',
                testId: 'csm-email-success',
                icon: 'approve',
            });

            onNextHandler?.();
        } catch (e) {
            notificationManager.addNotification({
                type: 'toast',
                title: 'Unable to send email',
                testId: 'csm-email-success',
                icon: 'error',
            });
        }
    };

    const accountMessage = accountOwnerData?.name
        ? ` ${accountOwnerData?.name}${
              accountOwnerData?.email ? ` (${accountOwnerData?.email})` : ''
          }.`
        : '';

    return (
        <FormProvider {...formController}>
            <form
                onSubmit={(ev) => ev.preventDefault()}
                style={{ height: '100%', overflow: 'auto' }}
            >
                <FlexContainer flexDirection="column" gap="16px" height="100%">
                    {isFetchingAccountOwnerData && (
                        <FlexContainer flexDirection="column" gap="8px">
                            <Skeleton isVisible height={16} width="80%" />
                            <Skeleton isVisible height={16} width="30%" />
                        </FlexContainer>
                    )}
                    {!isFetchingAccountOwnerData && (
                        <BodyText>
                            For any questions, contact your Customer Success Manager{accountMessage}
                        </BodyText>
                    )}
                    {!isFetchingAccountOwnerData && accountOwnerData?.name && (
                        <>
                            <FlexContainer flexDirection="column" gap="16px" height="100%">
                                <FlexItem>
                                    <ControlledInputField
                                        label="Subject"
                                        name="subject"
                                        placeholder="Subject"
                                        required
                                        asterix
                                        testId="email-subject"
                                    />
                                </FlexItem>
                                <FlexItem>
                                    <ControlledTextArea
                                        label="Message"
                                        name="message"
                                        placeholder="Please share what you'd like to ask"
                                        initialHeight={120}
                                        required
                                        asterix
                                        maxLength={1000}
                                        testId="email-message"
                                    />
                                </FlexItem>
                            </FlexContainer>

                            <FlexItem alignSelf="flex-end">
                                <Button
                                    size="small"
                                    disabled={!formController.formState.isValid}
                                    onClick={formController.handleSubmit(handleSubmit)}
                                    isLoading={isLoadingSendAccountOwnerEmailMutation}
                                    testId="submit-button"
                                    variant="primary-gray"
                                >
                                    Send Email
                                </Button>
                            </FlexItem>
                        </>
                    )}
                </FlexContainer>
            </form>
        </FormProvider>
    );
};
